import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Ticket,
  Users,
  DollarSign,
  Settings,
  LogOut,
  Plus,
  Trash2,
  Check,
  Clock,
  CalendarClock,
  ChevronDown,
  Search,
  X,
  QrCode,
  Camera,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Edit,
  Download,
  FileSpreadsheet,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  FormInput,
  Calendar,
  Mail,
  Send,
  Eye,
  Menu,
  Paperclip,
  Wrench,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../context/AuthContext";
import { Html5Qrcode } from "html5-qrcode";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { SessionManagement, CouponManagement, CustomFieldsManagement } from "../../components/AdminSettings";
import RichTextEditor from "../../components/RichTextEditor";

import { API_URL } from "../../config";
import { DataModal } from "./shared/DataModal";
import { SessionDataModal } from "./shared/SessionDataModal";


// ── Weekly Digest Card ─────────────────────────────────────────────
// Compact admin widget on the Dashboard with preview + send + auto-toggle.
const WeeklyDigestCard = () => {
  const [loading, setLoading] = useState(true);
  const [digest, setDigest] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [previewRes, settingsRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/weekly-digest/preview`, { withCredentials: true }),
          axios.get(`${API_URL}/api/admin/show`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setDigest(previewRes.data.digest);
        setEnabled(!!(settingsRes.data?.weekly_digest_enabled));
      } catch {
        // non-critical
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleEnabled = async (val) => {
    setSavingToggle(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/show`,
        { weekly_digest_enabled: val },
        { withCredentials: true },
      );
      setEnabled(val);
      toast.success(val ? "Envio semanal activado (sextas 09:00 UTC)" : "Envio semanal desactivado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar definição");
    } finally {
      setSavingToggle(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/weekly-digest/send-now`,
        {},
        { withCredentials: true },
      );
      toast.success(`Digest enviado para ${(data.recipients || []).join(", ") || "admin"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha no envio");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-900 border border-white/10 rounded-xl p-5 mb-8" data-testid="weekly-digest-card-loading">
        <p className="text-zinc-500 text-sm">A carregar dashboard semanal...</p>
      </div>
    );
  }

  const sales = digest?.sales || {};
  const insts = digest?.installments || {};
  const overdue = digest?.overdue || {};

  return (
    <>
      <div className="bg-gradient-to-br from-amber-950/30 via-zinc-900 to-zinc-900 border border-amber-500/20 rounded-xl p-5 mb-8" data-testid="weekly-digest-card">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <p className="text-amber-400 text-[10px] uppercase tracking-[2px] font-semibold">Dashboard semanal</p>
            <h3 className="text-white font-semibold mt-1">Resumo dos últimos 7 dias</h3>
            <p className="text-zinc-500 text-xs mt-0.5">{digest?.iso_week} · Enviado automaticamente à sexta-feira 09:00 UTC quando activo</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-zinc-400 text-xs hidden sm:inline">Auto-envio</span>
            <Switch
              checked={enabled}
              disabled={savingToggle}
              onCheckedChange={toggleEnabled}
              data-testid="weekly-digest-toggle"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          <div className="bg-black/30 border border-white/5 rounded-lg p-3" data-testid="weekly-digest-kpi-sales">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Vendas</p>
            <p className="text-xl font-bold text-white mt-0.5">{sales.count || 0}</p>
            <p className="text-[10px] text-zinc-500">{(sales.avg_ticket || 0).toFixed(2)}€ média</p>
          </div>
          <div className="bg-black/30 border border-emerald-500/15 rounded-lg p-3" data-testid="weekly-digest-kpi-revenue">
            <p className="text-[10px] uppercase tracking-wider text-emerald-400">Receita</p>
            <p className="text-xl font-bold text-emerald-300 mt-0.5">{(sales.revenue || 0).toFixed(2)}€</p>
            <p className="text-[10px] text-zinc-500">Bilhetes pagos</p>
          </div>
          <div className="bg-black/30 border border-white/5 rounded-lg p-3" data-testid="weekly-digest-kpi-installments">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Prestações</p>
            <p className="text-xl font-bold text-white mt-0.5">{insts.payments_count || 0}</p>
            <p className="text-[10px] text-zinc-500">{(insts.payments_amount || 0).toFixed(2)}€ recebidos</p>
          </div>
          <div className={`bg-black/30 border rounded-lg p-3 ${overdue.count ? "border-orange-500/30" : "border-white/5"}`} data-testid="weekly-digest-kpi-overdue">
            <p className={`text-[10px] uppercase tracking-wider ${overdue.count ? "text-orange-400" : "text-zinc-500"}`}>Em atraso</p>
            <p className={`text-xl font-bold mt-0.5 ${overdue.count ? "text-orange-300" : "text-white"}`}>{overdue.count || 0}</p>
            <p className="text-[10px] text-zinc-500">{(overdue.total_remaining || 0).toFixed(2)}€ em falta</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setPreviewOpen(true)}
            variant="outline"
            className="border-white/10 text-zinc-200 hover:bg-white/5"
            data-testid="weekly-digest-preview-btn"
          >
            <Eye className="w-4 h-4 mr-2" /> Pré-visualizar
          </Button>
          <Button
            onClick={sendNow}
            disabled={sending}
            className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
            data-testid="weekly-digest-send-btn"
          >
            <Send className="w-4 h-4 mr-2" /> {sending ? "A enviar..." : "Enviar agora"}
          </Button>
        </div>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="weekly-digest-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-400">Pré-visualização — {digest?.iso_week}</DialogTitle>
            <DialogDescription className="text-zinc-500">Resumo dos últimos 7 dias que será enviado por email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-black/40 rounded-lg p-3 border border-white/5">
                <p className="text-[10px] uppercase text-zinc-500">Vendas</p>
                <p className="text-xl font-bold text-white">{sales.count} <span className="text-sm text-zinc-400">· {(sales.revenue || 0).toFixed(2)}€</span></p>
              </div>
              <div className="bg-black/40 rounded-lg p-3 border border-white/5">
                <p className="text-[10px] uppercase text-zinc-500">Novos clientes</p>
                <p className="text-xl font-bold text-white">{digest?.new_users_count || 0}</p>
              </div>
              <div className="bg-black/40 rounded-lg p-3 border border-white/5">
                <p className="text-[10px] uppercase text-zinc-500">Novos planos</p>
                <p className="text-xl font-bold text-white">{insts.new_plans || 0}</p>
              </div>
              <div className="bg-black/40 rounded-lg p-3 border border-white/5">
                <p className="text-[10px] uppercase text-zinc-500">Em atraso</p>
                <p className="text-xl font-bold text-orange-300">{overdue.count || 0}</p>
              </div>
            </div>

            <div>
              <p className="text-amber-400 text-xs uppercase tracking-wider font-semibold mb-2">Top tipos de bilhete</p>
              {(digest?.top_ticket_types || []).length === 0 ? (
                <p className="text-zinc-500 text-sm">Sem vendas esta semana.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-zinc-500 text-[10px] uppercase">
                      <th className="text-left pb-1">Tipo</th>
                      <th className="text-right pb-1">Qtd</th>
                      <th className="text-right pb-1">Receita</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(digest?.top_ticket_types || []).map((t, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="py-1.5 text-zinc-200">{t.name}</td>
                        <td className="py-1.5 text-zinc-400 text-right">{t.count}</td>
                        <td className="py-1.5 text-amber-300 text-right font-medium">{(t.revenue || 0).toFixed(2)}€</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {(overdue.top || []).length > 0 && (
              <div>
                <p className="text-orange-400 text-xs uppercase tracking-wider font-semibold mb-2">Top em atraso</p>
                <table className="w-full text-sm">
                  <tbody>
                    {(overdue.top || []).map((p, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="py-1.5 text-zinc-200">{[p.first_name, p.last_name].filter(Boolean).join(" ") || p.email}</td>
                        <td className="py-1.5 text-orange-300 text-center text-xs">{p.days_overdue}d</td>
                        <td className="py-1.5 text-amber-300 text-right font-medium">{(p.remaining_due || 0).toFixed(2)}€</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};


// Pure helpers (outside component to avoid react-hooks/immutability false
// positives on Array.filter calls from the React 19 compiler).
const filterSales = (sales, filter) => {
  if (filter === "paid") return sales.filter(s => s.payment_status === "paid" && (s.final_price || 0) > 0);
  if (filter === "free") return sales.filter(s => s.payment_status === "paid" && (s.final_price || 0) === 0);
  // Pending in the Dashboard now bundles both `pending` (buyers who never paid)
  // and `installments` (partial payments) — matching the backend counter and
  // the "Pendente" label shown in Sales Management. See lib/paymentStatus.js.
  if (filter === "pending") return sales.filter(s => s.payment_status === "pending");
  if (filter === "installments") return sales.filter(s => s.payment_status === "installments");
  if (filter === "revenue") return sales.filter(s => s.payment_status === "paid");
  return sales;
};
const filterBySession = (sales, sessionId) => sales.filter(s => s.session_id === sessionId);



export const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [sales, setSales] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [visitStats, setVisitStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalType, setModalType] = useState(null); // "paid", "pending", "all"
  const [selectedSession, setSelectedSession] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, salesRes, sessionsRes, visitRes, roomsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/financial`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/sales`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/sessions/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/visit-stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/rooms`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setSales(salesRes.data);
      setSessions(sessionsRes.data);
      setVisitStats(visitRes.data);
      setRooms(Array.isArray(roomsRes.data) ? roomsRes.data : []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredSales = (filter) => filterSales(sales, filter);

  const getModalTitle = useCallback(() => {
    switch (modalType) {
      case "paid": return "Bilhetes Pagos (com dinheiro)";
      case "free": return "Bilhetes Gratuitos (cupão 100% / oferta)";
      case "pending": return "Bilhetes Pendentes (sem pagamento)";
      case "installments": return "Bilhetes em Prestações (pagamento parcial)";
      case "all": return "Todos os Registos";
      case "revenue": return `Vendas (Bilhetes): ${stats?.total_revenue?.toFixed(2)}€ (Confirmada) / ${stats?.pending_revenue?.toFixed(2)}€ (Pendente)`;
      default: return "";
    }
  }, [modalType, stats]);

  if (loading) {
    return <div className="animate-pulse">A carregar...</div>;
  }

  return (
    <div data-testid="dashboard-overview">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-zinc-500 text-sm mt-1">Vista geral da Plataforma</p>
        </div>
      </div>
      <h3 className="text-lg font-semibold text-white mb-4">Resumo de Bilhetes</h3>
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <div 
          onClick={() => setModalType("all")}
          className="bg-zinc-900 rounded-xl p-6 border border-white/10 cursor-pointer hover:border-amber-500/50 transition-colors"
          data-testid="card-total"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
              <Ticket className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-zinc-500 text-sm">Total Bilhetes</p>
              <p className="text-2xl font-bold text-blue-500">{stats?.total_registrations}</p>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-2">Clique para ver detalhes</p>
        </div>
        
        <div 
          onClick={() => setModalType("paid")}
          className="bg-zinc-900 rounded-xl p-6 border border-white/10 cursor-pointer hover:border-green-500/50 transition-colors"
          data-testid="card-paid"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
              <Check className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-zinc-500 text-sm">Pagos</p>
              <p className="text-2xl font-bold text-green-500">{stats?.paid}</p>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-2">Com dinheiro real</p>
        </div>

        <div
          onClick={() => setModalType("free")}
          className="bg-zinc-900 rounded-xl p-6 border border-white/10 cursor-pointer hover:border-pink-500/50 transition-colors"
          data-testid="card-free"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-pink-500/10 flex items-center justify-center">
              <span className="text-pink-400 text-xl font-bold">🎁</span>
            </div>
            <div>
              <p className="text-zinc-500 text-sm">Gratuitos</p>
              <p className="text-2xl font-bold text-pink-400">{stats?.paid_free || 0}</p>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-2">Cupão 100% / oferta</p>
        </div>

        <div 
          onClick={() => setModalType("pending")}
          className="bg-zinc-900 rounded-xl p-6 border border-white/10 cursor-pointer hover:border-yellow-500/50 transition-colors"
          data-testid="card-pending"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-yellow-500/10 flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <p className="text-zinc-500 text-sm">Pendentes</p>
              <p className="text-2xl font-bold text-yellow-500">{Math.max(0, (stats?.pending ?? 0) - (stats?.installments ?? 0))}</p>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-2">Sem qualquer pagamento</p>
        </div>

        <div 
          onClick={() => setModalType("installments")}
          className="bg-zinc-900 rounded-xl p-6 border border-white/10 cursor-pointer hover:border-orange-500/50 transition-colors"
          data-testid="card-installments"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-orange-500/10 flex items-center justify-center">
              <CalendarClock className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <p className="text-zinc-500 text-sm">Prestações</p>
              <p className="text-2xl font-bold text-orange-400">{stats?.installments ?? 0}</p>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-2">Pagamento parcial em curso</p>
        </div>
        
        <div 
          onClick={() => setModalType("revenue")}
          className="bg-zinc-900 rounded-xl p-6 border border-white/10 cursor-pointer hover:border-amber-500/50 transition-colors"
          data-testid="card-revenue"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center">
              <span className="text-purple-500 text-xl font-bold">€</span>
            </div>
            <div>
              <p className="text-zinc-500 text-sm">Vendas (Bilhetes)</p>
              <p className="text-2xl font-bold text-purple-500">{stats?.total_revenue?.toFixed(2)}€</p>
              <p className="text-[10px] text-zinc-600 mt-0.5">Bilhetes pagos · agregado</p>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-2">Clique para ver detalhes</p>
        </div>
      </div>

      <WeeklyDigestCard />

      {/* Vendas por Tipo de Sala */}
      {(() => {
        // Map session_id → room_id (from sessions/stats) and room_id → room name/order
        const sessionRoom = new Map();
        sessions.forEach((s) => sessionRoom.set(s.id, s.room_id || null));
        const roomMeta = new Map();
        rooms.forEach((r) => roomMeta.set(r.id, { name: r.name || "(sem nome)", order: r.order ?? 999 }));

        const breakdown = new Map();
        sales.forEach((s) => {
          const rid = sessionRoom.get(s.session_id) || null;
          const key = rid || "__none__";
          const label = rid ? (roomMeta.get(rid)?.name || "(sala removida)") : "(sem sala)";
          const order = rid ? (roomMeta.get(rid)?.order ?? 999) : 9999;
          const acc = breakdown.get(key) || { id: rid, name: label, order, total: 0, paid: 0, free: 0, pending: 0, installments: 0, revenue: 0 };
          acc.total += 1;
          if (s.payment_status === "paid") {
            const price = Number(s.final_price || 0);
            if (price > 0) { acc.paid += 1; acc.revenue += price; }
            else acc.free += 1;
          } else if (s.payment_status === "installments") acc.installments += 1;
          else if (s.payment_status === "pending") acc.pending += 1;
          breakdown.set(key, acc);
        });
        const rows = Array.from(breakdown.values()).sort((a, b) => a.order - b.order);
        if (rows.length === 0) return null;
        const totals = rows.reduce(
          (a, r) => ({
            total: a.total + r.total,
            paid: a.paid + r.paid,
            free: a.free + r.free,
            pending: a.pending + r.pending,
            installments: a.installments + r.installments,
            revenue: a.revenue + r.revenue,
          }),
          { total: 0, paid: 0, free: 0, pending: 0, installments: 0, revenue: 0 }
        );
        return (
          <div className="mb-8" data-testid="room-breakdown">
            <h3 className="text-lg font-semibold text-white mb-4">Vendas por Tipo de Sala</h3>
            <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead className="bg-zinc-950 text-zinc-400">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Sala</th>
                    <th className="text-right px-4 py-3 font-medium">Total</th>
                    <th className="text-right px-4 py-3 font-medium">Pagos</th>
                    <th className="text-right px-4 py-3 font-medium">Gratuitos</th>
                    <th className="text-right px-4 py-3 font-medium">Pendentes</th>
                    <th className="text-right px-4 py-3 font-medium">Prestações</th>
                    <th className="text-right px-4 py-3 font-medium">Receita</th>
                    <th className="text-right px-4 py-3 font-medium">% do Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id || "__none__"}
                      className="border-t border-white/5 cursor-pointer hover:bg-zinc-800/40 transition-colors"
                      onClick={() => r.id && navigate(`/admin/sales?room=${encodeURIComponent(r.id)}`)}
                      data-testid={`room-row-${r.id || "none"}`}
                      title={r.id ? "Clique para filtrar Gestão de Bilhetes por esta sala" : ""}
                    >
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-1 rounded-full bg-violet-500/10 text-violet-300 border border-violet-500/20">
                          {r.name}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-blue-400 font-medium">{r.total}</td>
                      <td className="px-4 py-3 text-right text-green-400 font-medium">{r.paid}</td>
                      <td className="px-4 py-3 text-right text-pink-400">{r.free || "—"}</td>
                      <td className="px-4 py-3 text-right text-yellow-400">{r.pending || "—"}</td>
                      <td className="px-4 py-3 text-right text-orange-400 font-medium">{r.installments || "—"}</td>
                      <td className="px-4 py-3 text-right text-purple-400 font-medium">{r.revenue.toFixed(2)}€</td>
                      <td className="px-4 py-3 text-right text-zinc-400">
                        {totals.total > 0 ? `${((r.total / totals.total) * 100).toFixed(0)}%` : "—"}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t border-white/10 bg-zinc-950/50">
                    <td className="px-4 py-3 text-zinc-400 uppercase text-xs font-semibold">Total</td>
                    <td className="px-4 py-3 text-right text-blue-400 font-bold">{totals.total}</td>
                    <td className="px-4 py-3 text-right text-green-400 font-bold">{totals.paid}</td>
                    <td className="px-4 py-3 text-right text-pink-400 font-bold">{totals.free || "—"}</td>
                    <td className="px-4 py-3 text-right text-yellow-400 font-bold">{totals.pending || "—"}</td>
                    <td className="px-4 py-3 text-right text-orange-400 font-bold">{totals.installments || "—"}</td>
                    <td className="px-4 py-3 text-right text-purple-400 font-bold">{totals.revenue.toFixed(2)}€</td>
                    <td className="px-4 py-3 text-right text-zinc-500">100%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Vendas por Tipo de Bilhete */}
      {(() => {
        const breakdown = new Map();
        sales.forEach((s) => {
          const key = s.ticket_type_name || "(sem tipo)";
          const acc = breakdown.get(key) || { name: key, total: 0, paid: 0, free: 0, pending: 0, installments: 0, revenue: 0 };
          acc.total += 1;
          if (s.payment_status === "paid") {
            const price = Number(s.final_price || 0);
            if (price > 0) {
              acc.paid += 1;
              acc.revenue += price;
            } else {
              acc.free += 1;
            }
          } else if (s.payment_status === "installments") {
            acc.installments += 1;
          } else if (s.payment_status === "pending") {
            acc.pending += 1;
          }
          breakdown.set(key, acc);
        });
        const rows = Array.from(breakdown.values()).sort((a, b) => b.total - a.total);
        if (rows.length === 0) return null;
        const totals = rows.reduce(
          (a, r) => ({
            total: a.total + r.total,
            paid: a.paid + r.paid,
            free: a.free + r.free,
            pending: a.pending + r.pending,
            installments: a.installments + r.installments,
            revenue: a.revenue + r.revenue,
          }),
          { total: 0, paid: 0, free: 0, pending: 0, installments: 0, revenue: 0 }
        );
        return (
          <div className="mb-8" data-testid="ticket-type-breakdown">
            <h3 className="text-lg font-semibold text-white mb-4">Vendas por Tipo de Bilhete</h3>
            <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead className="bg-zinc-950 text-zinc-400">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Tipo</th>
                    <th className="text-right px-4 py-3 font-medium">Total</th>
                    <th className="text-right px-4 py-3 font-medium">Pagos</th>
                    <th className="text-right px-4 py-3 font-medium">Gratuitos</th>
                    <th className="text-right px-4 py-3 font-medium">Pendentes</th>
                    <th className="text-right px-4 py-3 font-medium">Prestações</th>
                    <th className="text-right px-4 py-3 font-medium">Receita</th>
                    <th className="text-right px-4 py-3 font-medium">% do Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.name}
                      className="border-t border-white/5 cursor-pointer hover:bg-zinc-800/40 transition-colors"
                      onClick={() => navigate(`/admin/sales?ticket_type=${encodeURIComponent(r.name)}`)}
                      data-testid={`tt-row-${r.name}`}
                      title="Clique para filtrar Gestão de Bilhetes por este tipo"
                    >
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-1 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">
                          {r.name}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-blue-400 font-medium">{r.total}</td>
                      <td className="px-4 py-3 text-right text-green-400 font-medium">{r.paid}</td>
                      <td className="px-4 py-3 text-right text-pink-400">{r.free || "—"}</td>
                      <td className="px-4 py-3 text-right text-yellow-400">{r.pending || "—"}</td>
                      <td className="px-4 py-3 text-right text-orange-400 font-medium" data-testid={`tt-row-${r.name}-installments`}>{r.installments || "—"}</td>
                      <td className="px-4 py-3 text-right text-purple-400 font-medium">{r.revenue.toFixed(2)}€</td>
                      <td className="px-4 py-3 text-right text-zinc-400">
                        {totals.total > 0 ? `${((r.total / totals.total) * 100).toFixed(0)}%` : "—"}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t border-white/10 bg-zinc-950/50">
                    <td className="px-4 py-3 text-zinc-400 uppercase text-xs font-semibold">Total</td>
                    <td className="px-4 py-3 text-right text-blue-400 font-bold">{totals.total}</td>
                    <td className="px-4 py-3 text-right text-green-400 font-bold">{totals.paid}</td>
                    <td className="px-4 py-3 text-right text-pink-400 font-bold">{totals.free || "—"}</td>
                    <td className="px-4 py-3 text-right text-yellow-400 font-bold">{totals.pending || "—"}</td>
                    <td className="px-4 py-3 text-right text-orange-400 font-bold" data-testid="tt-total-installments">{totals.installments || "—"}</td>
                    <td className="px-4 py-3 text-right text-purple-400 font-bold">{totals.revenue.toFixed(2)}€</td>
                    <td className="px-4 py-3 text-right text-zinc-500">100%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      <h3 className="text-lg font-semibold text-white mb-4">Resumo de Sessões</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {(() => {
          // Reorder session_breakdown to match the admin "Salas, Sessões e Bilhetes"
          // page (sessions are returned by `/api/admin/sessions/stats` already
          // sorted by room.order → session.order). Items without a matching
          // session fall to the bottom preserving original order.
          const sessionIndex = new Map(sessions.map((s, i) => [s.id, i]));
          const sorted = [...(stats?.session_breakdown || [])].sort((a, b) => {
            const ia = sessionIndex.has(a.session_id) ? sessionIndex.get(a.session_id) : 10 ** 9;
            const ib = sessionIndex.has(b.session_id) ? sessionIndex.get(b.session_id) : 10 ** 9;
            return ia - ib;
          });
          return sorted.map((session, index) => {
          const fullSession = sessions.find(s => s.id === session.session_id);
          return (
            <div 
              key={session.session_id} 
              onClick={() => setSelectedSession({ ...session, ...fullSession })}
              className={`bg-zinc-900 rounded-xl p-6 border cursor-pointer hover:border-amber-500/50 transition-colors ${session.active === false ? "border-red-500/20 opacity-70" : "border-white/10"}`}
              data-testid={`session-card-${index}`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-amber-500 text-sm font-medium">
                  {fullSession?.name || `Sessão ${index + 1}`}
                </p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${session.active === false ? "bg-red-500/10 text-red-400 border border-red-500/20" : "bg-green-500/10 text-green-400 border border-green-500/20"}`}>
                  {session.active === false ? "Oculta" : "Visível"}
                </span>
              </div>
              {fullSession?.subtitle && (
                <p className="text-zinc-300 text-sm font-medium uppercase tracking-wider mb-1">{fullSession.subtitle}</p>
              )}
              <p className="text-white font-semibold">{session.date}</p>
              <p className="text-zinc-500 text-sm mb-4">{session.time}</p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Vendidos</span>
                  <span className="text-white">{session.sold} / {session.total_seats}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Disponíveis</span>
                  <span className="text-white">{session.available_seats}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Receita</span>
                  <span className="text-amber-500 font-medium">{session.revenue?.toFixed(2)}€</span>
                </div>
              </div>
              <p className="text-xs text-zinc-500 mt-3">Clique para ver detalhes</p>
            </div>
          );
        });
        })()}
      </div>

      {/* Visit Stats */}
      {visitStats && (
        <>
          <h3 className="text-lg font-semibold text-white mb-4 mt-8">Estatísticas de Visitas</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
              <p className="text-zinc-500 text-sm">Total Acessos Geral</p>
              <p className="text-2xl font-bold text-blue-500">{visitStats.total_visits}</p>
            </div>
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
              <p className="text-zinc-500 text-sm">Total Acessos Geral Hoje</p>
              <p className="text-2xl font-bold text-green-400">{visitStats.today_visits}</p>
            </div>
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
              <p className="text-zinc-500 text-sm">Total Visitas Únicos (IPs)</p>
              <p className="text-2xl font-bold text-purple-500">{visitStats.unique_visitors}</p>
            </div>
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
              <p className="text-zinc-500 text-sm">Total Visitas Únicos (IPs) Hoje</p>
              <p className="text-2xl font-bold text-amber-500">{visitStats.unique_today}</p>
            </div>
          </div>
          <div className="bg-zinc-900 rounded-xl p-6 border border-white/10 mb-8">
            <p className="text-zinc-400 text-sm mb-4">Visitas nos últimos 30 dias</p>
            <div className="flex items-end gap-1 h-32">
              {[...visitStats.daily].reverse().map((d, i) => {
                const max = Math.max(...visitStats.daily.map(x => x.count), 1);
                const height = (d.count / max) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                    <div className="absolute -top-6 bg-zinc-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                      {d.date}: {d.count}
                    </div>
                    <div
                      className="w-full rounded-t transition-all hover:opacity-80"
                      style={{ height: `${Math.max(height, 2)}%`, backgroundColor: "#3b82f6" }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-2">
              <span className="text-zinc-600 text-xs">{visitStats.daily[visitStats.daily.length - 1]?.date}</span>
              <span className="text-zinc-600 text-xs">{visitStats.daily[0]?.date}</span>
            </div>
          </div>
        </>
      )}

      {/* Data Modal */}
      <DataModal
        isOpen={!!modalType}
        onClose={() => setModalType(null)}
        title={getModalTitle()}
        data={modalType ? getFilteredSales(modalType) : []}
        sessions={sessions}
      />

      {/* Session Modal */}
      <SessionDataModal
        isOpen={!!selectedSession}
        onClose={() => setSelectedSession(null)}
        session={selectedSession}
        data={selectedSession ? filterBySession(sales, selectedSession.session_id) : []}
      />
    </div>
  );
};

// Edit Sale Modal Component
